import { ITEM_STATUS_CANCELLED, ITEM_STATUS_FAILED } from 'constants/cart'

/**
 * Free Société lounge passes an Infinite member can redeem on one booking.
 * The allowance is shared by the original purchase and later add-ons.
 * Paid lounge passes are not part of this cap.
 */
export const INFINITE_FREE_LOUNGE_PASSES_PER_BOOKING = 4

const INACTIVE_EXPERIENCE_ITEM_STATUSES = new Set<string>([ITEM_STATUS_CANCELLED, ITEM_STATUS_FAILED])

interface LoungePassRedemption {
  code: string
  status: string
  itemId: string
}

interface ExperienceItemStatus {
  id: string
  status: string
}

/**
 * Issued free lounge passes still attached to the booking.
 * Cancelled and failed experience items no longer consume the allowance.
 */
export function countActiveFreeLoungePassRedemptions(
  benefitsRedeemed: ReadonlyArray<LoungePassRedemption> | undefined,
  experienceItems: ReadonlyArray<ExperienceItemStatus> | undefined,
): number {
  const inactiveItemIds = new Set(
    (experienceItems ?? [])
      .filter(item => INACTIVE_EXPERIENCE_ITEM_STATUSES.has(item.status))
      .map(item => item.id),
  )

  return (benefitsRedeemed ?? []).filter(benefit =>
    benefit.code === 'lounge_pass'
    && benefit.status === 'ISSUED'
    && !inactiveItemIds.has(benefit.itemId),
  ).length
}

/**
 * A fresh checkout cart is the booking being purchased. A cart opened to add
 * something to an existing order only counts when it belongs to that order,
 * so an unrelated in-progress cart does not consume this booking's allowance.
 */
export function cartCountsTowardsBookingLoungeCap(
  bookingOrderId: string | undefined,
  cartOrderId: string | undefined,
): boolean {
  if (!bookingOrderId) return true
  return cartOrderId === bookingOrderId
}

interface FreeLoungePassRemainingInput {
  isInfinite: boolean
  /** Account benefit availability. `Infinity` when the tier benefit is unlimited. */
  benefitAvailability: number
  redeemedOnOrder: number
  /**
   * Free passes in this booking's cart, excluding the selection being edited.
   * An unrelated cart does not count towards the Infinite per-booking cap.
   */
  freePassesInCart: number
  /**
   * Free passes in any cart. These consume a finite account allowance.
   * Defaults to `freePassesInCart`.
   */
  accountFreePassesInCart?: number
  /** Free passes currently selected in the lounge modal. */
  freePassesSelected: number
}

/**
 * Free passes that can still be added. Non-infinite members keep their account
 * allowance. Infinite members are also limited per booking, including passes
 * already redeemed on the order.
 */
export function getFreeLoungePassesRemainingOnBooking({
  isInfinite,
  benefitAvailability,
  redeemedOnOrder,
  freePassesInCart,
  accountFreePassesInCart,
  freePassesSelected,
}: FreeLoungePassRemainingInput): number {
  const selectedAndBookingCart = freePassesInCart + freePassesSelected
  const accountRemaining = Math.max(
    0,
    benefitAvailability - (accountFreePassesInCart ?? freePassesInCart) - freePassesSelected,
  )
  if (!isInfinite) return accountRemaining

  const bookingRemaining = Math.max(
    0,
    INFINITE_FREE_LOUNGE_PASSES_PER_BOOKING - redeemedOnOrder - selectedAndBookingCart,
  )
  return Math.min(accountRemaining, bookingRemaining)
}

export function exceedsInfiniteFreeLoungePassBookingCap(
  isInfinite: boolean,
  redeemedOnOrder: number,
  freePassesInCart: number,
): boolean {
  if (!isInfinite) return false
  return redeemedOnOrder + freePassesInCart > INFINITE_FREE_LOUNGE_PASSES_PER_BOOKING
}

/**
 * A booking that already has more than four free passes must not block a later
 * checkout that is not adding another free pass. Paid passes are not in this count.
 */
export function cartFreeLoungePassesExceedBookingCap(
  isInfinite: boolean,
  redeemedOnOrder: number,
  freePassesInCart: number,
): boolean {
  if (freePassesInCart <= 0) return false
  return exceedsInfiniteFreeLoungePassBookingCap(isInfinite, redeemedOnOrder, freePassesInCart)
}

/**
 * Infinite members have no account-level remaining count, so post-purchase
 * lounge rows stay visible unless this booking has already taken its free passes.
 * Paid passes are not loyalty redemptions and do not count.
 */
export function hasExhaustedInfiniteFreeLoungePassBookingAllowance(
  isInfinite: boolean,
  benefitsRedeemed: ReadonlyArray<LoungePassRedemption> | undefined,
  experienceItems: ReadonlyArray<ExperienceItemStatus> | undefined,
): boolean {
  if (!isInfinite) return false
  return countActiveFreeLoungePassRedemptions(benefitsRedeemed, experienceItems) >= INFINITE_FREE_LOUNGE_PASSES_PER_BOOKING
}

/**
 * Summary orders and a failed loyalty-details load have no redeemed-pass count.
 * Infinite members must not treat that missing count as zero. Other tiers do not use it.
 */
export function isInfiniteLoungePassRedemptionCountKnown(
  isInfinite: boolean,
  order: { type: string, loyaltyDetails?: { benefitsLoaded?: boolean } } | undefined,
): boolean {
  if (!isInfinite) return true
  if (!order || order.type !== 'full') return false
  return order.loyaltyDetails?.benefitsLoaded !== false
}

/**
 * A missing lounge benefit must not grant free passes.
 * Infinite availability is unlimited, so a missing count still falls through to the booking cap.
 */
export function getLoungePassBenefitAvailability(isInfinite: boolean, availability: number | undefined): number {
  if (availability !== undefined) return availability
  return isInfinite ? Infinity : 0
}
